import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { exitExamApi } from '../api';
import Header from './Header';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';

// Add the autoTable plugin to jsPDF
jsPDF.API.autoTable = autoTable;

export default function ExitExamResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [complaintData, setComplaintData] = useState({
    category: '',
    description: '',
    phoneNumber: ''
  });
  const [complaintReference, setComplaintReference] = useState('');
  const [showPdfPopup, setShowPdfPopup] = useState(false);
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isSubmittingComplaint, setIsSubmittingComplaint] = useState(false);

  const complaintCategories = [
    { value: 'result_not_found', label: 'My result not found' },
    { value: 'disqualified_no_reason', label: 'My result disqualified without reason' },
    { value: 'attendance_ng_grade', label: "I've signed attendance but my grade is NG" },
    { value: 'wrong_gender', label: 'Wrong gender information' },
    { value: 'other', label: 'Other' }
  ];

  // Helper function to sort results by year (descending) and period (End Year before Mid Year)
  const sortResultsByYear = (results) => {
    if (!Array.isArray(results)) {
      return results;
    }
    
    return [...results].sort((a, b) => {
      // Extract year from year field (e.g., "2018 Mid Year" -> 2018)
      const getYear = (yearStr) => {
        if (!yearStr) return 0;
        const match = yearStr.toString().match(/(\d{4})/);
        return match ? parseInt(match[1]) : 0;
      };
      
      // Extract period priority (End Year = 1, Mid Year = 2, so End Year comes first for same year)
      const getPeriodPriority = (yearStr) => {
        if (!yearStr) return 0;
        const str = yearStr.toString().toLowerCase();
        if (str.includes('end')) return 1;
        if (str.includes('mid')) return 2;
        return 0;
      };
      
      const yearA = getYear(a.year);
      const yearB = getYear(b.year);
      
      // First sort by year (descending - newest first)
      if (yearB !== yearA) {
        return yearB - yearA;
      }
      
      // If same year, sort by period (End Year before Mid Year)
      const periodA = getPeriodPriority(a.year);
      const periodB = getPeriodPriority(b.year);
      return periodA - periodB;
    });
  };

  const is2018EndYearPendingInResults = (results) => {
    const items = Array.isArray(results)
      ? results
      : results && typeof results === 'object'
        ? [results]
        : [];

    return items.some((item) => {
      const is2018EndYear = String(item?.year || '').trim() === '2018 End Year';
      const isPending =
        typeof item?.total_score === 'string' &&
        item.total_score.trim().toLowerCase() === 'result on pending';
      return is2018EndYear && isPending;
    });
  };

  const handleComplaintSubmit = async () => {
    if (is2018EndYearPendingInResults(result?.results)) {
      setValidationError('Complaints are unavailable while your 2018 End Year result is on pending.');
      return;
    }

    if (!complaintData.category || !complaintData.description.trim() || !complaintData.phoneNumber.trim()) {
      setValidationError('Please fill in all required fields.');
      return;
    }

    // Validate phone number length (max 10 characters)
    if (complaintData.phoneNumber.trim().length > 10) {
      setValidationError('Phone number must not exceed 10 characters.');
      return;
    }

    setValidationError(''); // Clear any previous errors
    setIsSubmittingComplaint(true); // Start loading

    try {
      const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
      const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

      const response = await fetch(`${API_URL}/complaints/submit`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: result.username,
          full_name: result.full_name || 'N/A',
          category: complaintData.category,
          description: complaintData.description,
          phoneNumber: complaintData.phoneNumber
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        // Success - close modal and show success message
        setShowComplaintModal(false);
        setShowSuccessModal(true);
        setComplaintData({ category: '', description: '', phoneNumber: '' });
        setValidationError(''); // Clear any errors
        // Store the reference number from backend response
        setComplaintReference(data.data?.reference_number || 'N/A');
      } else {
        // Error from server
        setValidationError(data.message || 'Failed to submit complaint. Please try again.');
      }
    } catch (error) {
      console.error('Complaint submission error:', error);
      setValidationError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmittingComplaint(false); // Stop loading
    }
  };

  const handleCancelComplaint = () => {
    setShowComplaintModal(false);
    setComplaintData({ category: '', description: '', phoneNumber: '' });
    setValidationError('');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const generatePDF = async () => {
    if (!result) {
      console.error('No result data available');
      return;
    }

    setIsDownloading(true);

    // Generate QR code for verification
    const verificationUrl = `${window.location.origin}/verify-exit-exam/${result.username}`;
    const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
      width: 80,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#ffffff'
      }
    });

    // Helper function to load and convert image to base64
    const loadImageToBase64 = async (imagePath, fallbackSvg) => {
    try {
        console.log(`Attempting to load image from ${imagePath}`);
      
        // Try multiple approaches to load the image
        let imageResponse;
      try {
          imageResponse = await fetch(imagePath);
      } catch (fetchError) {
        console.log('Fetch failed, trying alternative path');
          imageResponse = await fetch(`.${imagePath}`);
      }
      
        console.log(`Image response status: ${imageResponse.status}`);
      
        if (!imageResponse.ok) {
          throw new Error(`HTTP error! status: ${imageResponse.status}`);
      }
      
        const imageBlob = await imageResponse.blob();
        console.log(`Image blob size: ${imageBlob.size}`);
      
      // Create a canvas to ensure the image loads properly
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
        return await new Promise((resolve, reject) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
            console.log(`Image converted to base64 successfully via canvas`);
          resolve(dataUrl);
        };
        img.onerror = () => {
          console.log('Image failed to load, using fallback');
          reject(new Error('Image failed to load'));
        };
          img.src = URL.createObjectURL(imageBlob);
      });
    } catch (error) {
        console.warn(`Could not load image from ${imagePath}, using fallback:`, error);
        return fallbackSvg;
      }
    };

    // Load the main logo (left side)
    const logoFallback = 'data:image/svg+xml;base64,' + btoa(`
        <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#1e40af;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#3b82f6;stop-opacity:1" />
            </linearGradient>
          </defs>
          <rect width="80" height="80" rx="16" fill="url(#logoGradient)"/>
          <text x="40" y="50" font-family="Arial, sans-serif" font-size="32" font-weight="bold" text-anchor="middle" fill="white">M</text>
        </svg>
      `);
    
    const logoDataUrl = await loadImageToBase64('/logo.png', logoFallback);

    // Load the EAES logo (right side)
    const eaesFallback = 'data:image/svg+xml;base64,' + btoa(`
      <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="eaesGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#059669;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#10b981;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="80" height="80" rx="16" fill="url(#eaesGradient)"/>
        <text x="40" y="50" font-family="Arial, sans-serif" font-size="24" font-weight="bold" text-anchor="middle" fill="white">EAES</text>
      </svg>
    `);
    
    const eaesLogoDataUrl = await loadImageToBase64('/eaes.png', eaesFallback);

    // Create a temporary div with inline styles only
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = `
    <div style="width: 210mm; height: 297mm; background: #ffffff; position: relative; overflow: hidden; font-family: 'Inter', Arial, sans-serif; margin: 20px; display: flex; flex-direction: column;">
        
        <!-- Decorative Corner Clip -->
        <div style="position: absolute; top: 0; left: 0; width: 180px; height: 180px; overflow: hidden;">
            <div style="position: absolute; left: -68px; top: 68px; width: 270px; transform: rotate(-45deg); transform-origin: top left; box-shadow: 0 6px 15px rgba(0,0,0,0.3);">
                <!-- Deep Orange stripe (thinnest) -->
                <div style="height: 12px; background: linear-gradient(135deg, #782c07, #f97316); margin-bottom: 10px; border-radius: 6px;"></div>
                <!-- Red stripe (medium) -->
                <div style="height: 24px; background: linear-gradient(135deg, #6e1010, #ef4444); margin-bottom: 10px; border-radius: 9px;"></div>
                <!-- Blue stripe (thickest) -->
                <div style="height: 45px; background: linear-gradient(135deg, #1d3368, #3b82f6); border-radius: 12px;"></div>
            </div>
        </div>
        
        <!-- Watermark -->
        <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 120px; color: rgba(0, 0, 0, 0.1); font-weight: bold; z-index: 50; pointer-events: none; letter-spacing: 8px;">TEMPORARY</div>
        
        <!-- Header Section -->
        <section style="text-align: center; margin-bottom: 20px; padding-top: 50px;">
            <div style="display: flex; justify-content: center; align-items: center; margin-bottom: 16px; gap: 40px;">
                <!-- Left Logo -->
                <img src="${logoDataUrl}" alt="Ministry Logo" style="width: 100px; height: 100px; border-radius: 16px; box-shadow: 0 8px 25px rgba(30, 64, 175, 0.3);">
                
                <!-- Center Text -->
                <div style="display: flex; flex-direction: column; justify-content: center; text-align: center;">
                    <h1 style="font-size: 20px; font-weight: 700; color: #1f2937; margin: 0; letter-spacing: -0.5px;">የኤ.ፊ.ድ.ሪ ትምህርት ሚኒስቴር</h1>
                    <h1 style="font-size: 18px; font-weight: 600; color: #374151; margin: 4px 0; letter-spacing: -0.3px;">FDRE Ministry of Education</h1>
                </div>
                
                <!-- Right Logo (EAES) -->
                <img src="${eaesLogoDataUrl}" alt="EAES Logo" style="width: 120px; height: 120px; border-radius: 16px; box-shadow: 0 8px 25px rgba(5, 150, 105, 0.3);">
            </div>
            
            <!-- Gradient Separator -->
            <div style="height: 4px; background: linear-gradient(90deg, #1e40af, #3b82f6, #f97316, #ea580c); margin: 0 40px 20px 40px; border-radius: 2px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);"></div>
        </section>
        
        <!-- Document Body -->
        <main style="padding: 0 40px; position: relative; z-index: 10; flex: 1; display: flex; flex-direction: column;">
            
            <!-- Certificate Title -->
            <section style="text-align: center; margin-bottom: 20px;">
                <h2 style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 6px;">Exit Examination Certificate</h2>
            </section>

            <!-- Student Information Section -->
            <section style="margin-bottom: 20px;">
                <h3 style="font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px;">Student Information</h3>
                
                <!-- Row 1: Full Name and Username -->
                <div style="display: flex; gap: 20px; margin-bottom: 12px;">
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Full Name:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 8px;">${result.full_name || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Username:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 8px;">${(result.username || 'N/A').toUpperCase()}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Row 2: Institution and Gender -->
                <div style="display: flex; gap: 20px; margin-bottom: 12px;">
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Institution:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 8px;">${result.institution_name || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Gender:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 8px;">${result.gender || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Row 3: Exam Topic -->
                <div style="display: flex; gap: 20px; margin-bottom: 12px;">
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Exam Topic:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 8px;">${result.exam_topic_name || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Results Table Section -->
            <section style="margin-bottom: 10px;">
                <h3 style="font-size: 12px; font-weight: 600; color: #1f2937; margin-bottom: 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px;">Examination Results</h3>
                <div style="background: white; border: 1px solid #9ca3af; border-radius: 6px; overflow: hidden; position: relative;">
                    <table style="width: 100%; position: relative; z-index: 10; background: white; border-collapse: collapse; border: 1px solid #9ca3af;">
                        <thead style="background: linear-gradient(135deg, #60a5fa, #3b82f6); color: white;">
                            <tr>
                                <th style="padding: 6px 10px; text-align: left; font-weight: 600; font-size: 9px; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 2px solid #1e40af; border-right: 1px solid rgba(255,255,255,0.3);">Examination Year</th>
                                <th style="padding: 6px 10px; text-align: center; font-weight: 600; font-size: 9px; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 2px solid #1e40af; border-right: 1px solid rgba(255,255,255,0.3);">Total Score (%)</th>
                                <th style="padding: 6px 10px; text-align: center; font-weight: 600; font-size: 9px; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 2px solid #1e40af;">Status</th>
                            </tr>
                        </thead>
                        <tbody style="background: white;">
                            ${sortResultsByYear(result.results || []).map((resultItem, index) => {
                              const scoreNum = typeof resultItem.total_score === 'string' ? parseFloat(resultItem.total_score) : (resultItem.total_score || 0);
                              return `
                                <tr>
                                    <td style="padding: 6px 10px; color: #111827; font-weight: 500; background: white; font-size: 10px; border-right: 1px solid #d1d5db; border-bottom: 1px solid #9ca3af;">${resultItem.year || 'N/A'}</td>
                                    <td style="padding: 6px 10px; text-align: center; background: white; font-size: 10px; border-right: 1px solid #d1d5db; border-bottom: 1px solid #9ca3af;">
                                        <span style="font-size: 11px; font-weight: bold; ${scoreNum >= 50 ? 'color: #059669' : 'color: #dc2626'}">${isNgResult(resultItem) ? 'NG' : (String(resultItem.year || '').trim() === '2018 End Year' && typeof resultItem.total_score === 'string' && resultItem.total_score.trim().toLowerCase() === 'result on pending') ? resultItem.total_score : (resultItem.total_score !== null && resultItem.total_score !== undefined ? resultItem.total_score + '%' : '0%')}</span>
                                    </td>
                                    <td style="padding: 6px 10px; text-align: center; background: white; font-size: 10px; border-bottom: 1px solid #9ca3af;">
                                        ${resultItem.status === 'Pass' && resultItem.is_disqualified !== 1
                                            ? `<span style="color: #059669; font-weight: 700; font-size: 10px;">PASS</span>`
                                            : `<span style="display: inline-block; background: #fef2f2; color: #b91c1c; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-size: 9px;">${resultItem.is_disqualified === 1 ? `${(resultItem.status || '').toUpperCase()} (DISQUALIFIED)` : (resultItem.status || 'N/A').toUpperCase()}</span>`
                                        }
                                    </td>
                                </tr>
                              `;
                            }).join('')}
                        </tbody>
                    </table>
                </div>
            </section>

            <!-- Certification Statement -->
            <section style="margin-bottom: 16px;">
                <div style="background: linear-gradient(135deg, #eff6ff, #e0e7ff); border-left: 4px solid #2563eb; padding: 16px; border-radius: 0 12px 12px 0; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                    <p style="font-size: 14px; color: #1f2937; font-weight: 500; margin: 0; line-height: 1.4;">
                        This is to certify that the above-mentioned student has successfully completed the Exit Examination 
                        as required by the Ministry of Education, Federal Democratic Republic of Ethiopia.
                    </p>
                </div>
            </section>

            <!-- QR Code Verification Section -->
            <section style="margin-bottom: 16px; margin-top: 40px;">
                <div style="display: flex; align-items: center; justify-content: flex-end; gap: 20px; padding-right: 40px;">
                    <div style="text-align: center;">
                        <div style="width: 80px; height: 80px; background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin: 0 auto 8px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                            <img src="${qrCodeDataUrl}" alt="Verification QR Code" style="width: 70px; height: 70px;">
                        </div>
                        <p style="font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 2px;">Scan to Verify</p>
                        <p style="font-size: 10px; color: #6b7280;">Certificate Verification</p>
                    </div>
                    <div style="text-align: left;">
                        <div style="display: flex; align-items: center; margin-bottom: 6px;">
                            <span style="font-size: 12px; font-weight: 600; color: #374151;">Certificate Verification</span>
                        </div>
                        <p style="font-size: 10px; color: #6b7280; line-height: 1.4; max-width: 180px;">
                            Scan this QR code to verify the authenticity of this certificate 
                            through the official Ministry of Education portal.
                        </p>
                    </div>
                </div>
            </section>
        </main>

        <!-- Footer Section -->
        <footer style="background: linear-gradient(135deg, #f9fafb, #f3f4f6); padding: 16px 40px 20px; border-top: 1px solid #e5e7eb; position: relative; z-index: 20; border-radius: 0 0 12px 12px; margin-top: 20px;">
            <div style="text-align: center;">
                <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
                    <svg style="width: 16px; height: 16px; margin-right: 8px; fill: #3b82f6;" viewBox="0 0 24 24">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                    </svg>
                    <span style="font-size: 12px; color: #6b7280;">Generated on: ${new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                </div>
                <div style="border-top: 1px solid #d1d5db; padding-top: 12px;">
                    <p style="font-size: 11px; color: #6b7280; font-weight: 500; margin-bottom: 6px;">OFFICIAL DISCLAIMER</p>
                    <p style="font-size: 11px; color: #6b7280; line-height: 1.4; background: rgba(255, 255, 255, 0.7); padding: 8px 16px; border-radius: 8px; border: 1px solid #e5e7eb; margin: 0; backdrop-filter: blur(4px);">
                        This certificate is generated electronically by the Ministry of Education Exit Examination Portal. 
                        Any alterations or modifications to this document will render it invalid. For verification purposes, 
                        please visit https://result.ethernet.edu.et
                    </p>
                </div>
                
                <!-- Gradient Separator -->
                <div style="height: 4px; background: linear-gradient(90deg, #1e40af, #3b82f6, #f97316, #ea580c); margin: 16px 0 8px 0; border-radius: 2px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);"></div>
                
                <div style="padding-top: 8px; margin-top: 8px;">
                    <p style="font-size: 10px; color: #9ca3af; margin: 0;">
                        Federal Democratic Republic of Ethiopia, Ministry of Education | P.O. Box 1367, Addis Ababa
                    </p>
                </div>
            </div>
        </footer>
    </div>
    `;

    // Add the div to the document temporarily
    document.body.appendChild(tempDiv);

    try {
      // Convert the div to canvas
      const canvas = await html2canvas(tempDiv.firstElementChild, {
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794, // A4 width in pixels at 96 DPI
        height: 1123, // A4 height in pixels at 96 DPI
        scrollX: 0,
        scrollY: 0,
        logging: true // Enable logging to debug
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const imgHeight = 297; // A4 height in mm

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      // Download the PDF
      const fileName = `exit_exam_result_${result.username}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      // Clean up
      document.body.removeChild(tempDiv);
      setIsDownloading(false);
    }
  };

  const handleClosePdfPopup = () => {
    setShowPdfPopup(false);
  };

  const copyReferenceNumber = async () => {
    try {
      await navigator.clipboard.writeText(complaintReference);
      setCopyConfirmed(true);
      setTimeout(() => setCopyConfirmed(false), 2000); // Hide confirmation after 2 seconds
    } catch (err) {
      console.error('Failed to copy reference number:', err);
      alert('Failed to copy reference number. Please copy it manually.');
    }
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const fetchResult = async () => {
      const username = location?.state?.username;
      if (!username) {
        setError('No username provided');
        return;
      }

      // Check if we already have the result data
      const resultData = location?.state?.resultData;
      if (resultData) {
        setResult(resultData);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await exitExamApi.getResult(username);
        console.log('Exit Exam API Response:', response);
        if (response.status === 'success') {
          console.log('Setting result data:', response.data);
          console.log('Results array in response.data:', response.data?.results);
          setResult(response.data);
        } else {
          setError(response.message || 'Failed to fetch result');
        }
      } catch (err) {
        console.error('Error fetching result:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'rgb(18,82,166)' }}></div>
          <p className="text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Check if it's a "student not found" type of error
    const isStudentNotFound = error.toLowerCase().includes('student') || 
                             error.toLowerCase().includes('not found') || 
                             error.toLowerCase().includes('no result');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No Result Record Found</h2>
          <p className="text-gray-600 mb-4">No result record found for this username. Please check the username and try again.</p>
          <button
            onClick={() => navigate('/')}
            className="text-white px-6 py-2 rounded-lg"
            style={{ backgroundColor: 'rgb(18,82,166)' }}
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No Result Record Found</h2>
          <p className="text-gray-600 mb-4">No result record found for this username. Please check the username and try again.</p>
        <button
          onClick={() => navigate('/')}
            className="text-white px-6 py-2 rounded-lg"
            style={{ backgroundColor: 'rgb(18,82,166)' }}
        >
          Go Back Home
        </button>
        </div>
      </div>
    );
  }

  // Get results array, default to empty array if not present
  // Handle both array format and ensure it's always an array
  let resultsArray = [];
  if (result && result.results) {
    if (Array.isArray(result.results)) {
      resultsArray = result.results;
    } else if (typeof result.results === 'object') {
      // If results is a single object, wrap it in an array
      resultsArray = [result.results];
    }
  }
  
  // Sort results by year in descending order (newest first)
  // Within the same year, End Year comes before Mid Year
  resultsArray = sortResultsByYear(resultsArray);

  const is2018EndYearResult = (item) => String(item?.year || '').trim() === '2018 End Year';
  const is2018EndYearDisqualified = resultsArray.some(
    (item) => is2018EndYearResult(item) && item.is_disqualified === 1
  );

  // Debug: Log the results array at render time
  console.log('Render - result object:', result);
  console.log('Render - result.results:', result?.results);
  console.log('Render - resultsArray:', resultsArray);
  console.log('Render - resultsArray.length:', resultsArray.length);
  console.log('Render - Array.isArray(resultsArray):', Array.isArray(resultsArray));
  
  // Helper function to convert total_score string to number
  const getScoreNumber = (score) => {
    if (score === null || score === undefined) return 0;
    if (typeof score === 'string') {
      const parsed = parseFloat(score);
      return isNaN(parsed) ? 0 : parsed;
    }
    return typeof score === 'number' ? score : 0;
  };

  const isNgValue = (value) =>
    typeof value === 'string' && value.trim().toUpperCase() === 'NG';

  const isNgResult = (resultItem) =>
    resultItem?.status === 'NG' || isNgValue(resultItem?.total_score);

  const isPendingScoreValue = (value) =>
    typeof value === 'string' && value.trim().toLowerCase() === 'result on pending';

  const isPendingResult = (resultItem) =>
    is2018EndYearResult(resultItem) && isPendingScoreValue(resultItem?.total_score);

  const is2018EndYearPending = resultsArray.some((item) => isPendingResult(item));
  const isComplaintAllowed = !is2018EndYearPending;

  const formatTotalScore = (resultItem) => {
    if (isNgResult(resultItem)) return 'NG';
    if (isPendingResult(resultItem)) return String(resultItem.total_score).trim();
    return resultItem?.total_score !== null && resultItem?.total_score !== undefined
      ? `${resultItem.total_score}%`
      : '0%';
  };

  const isFlagActive = (value) => value === 1 || value === '1';

  const render2018EndYearTags = (resultItem) => {
    if (!is2018EndYearResult(resultItem)) return null;

    const tags = [];
    if (isFlagActive(resultItem.is_absent)) {
      tags.push({
        key: 'absent',
        label: 'Attendance Absent',
        icon: 'fa-user-slash',
        className: 'bg-amber-50 text-amber-800 border-amber-200',
      });
    }
    if (isFlagActive(resultItem.supervisor_report)) {
      tags.push({
        key: 'cheating',
        label: 'Cheating Reported',
        icon: 'fa-triangle-exclamation',
        className: 'bg-red-50 text-red-800 border-red-200',
      });
    }

    if (tags.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {tags.map((tag) => (
          <span
            key={tag.key}
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border shadow-sm ${tag.className}`}
          >
            <i className={`fa-solid ${tag.icon} mr-1.5`} />
            {tag.label}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/usernamepage')}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            <span>Back</span>
          </button>
        </div>

        {/* Results Header */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h2 className="text-xl font-bold flex items-center mb-4 sm:mb-0" style={{ color: 'rgb(18,82,166)' }}>
              <i className="fa-solid fa-graduation-cap mr-3 text-lg" style={{ color: 'rgb(18,82,166)' }} />
              Exit Exam Result
              </h2>
            <div className="flex gap-3">
              <button
                onClick={generatePDF}
                disabled={isDownloading || !result || !resultsArray || resultsArray.length === 0}
                className="inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'rgb(18,82,166)' }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = 'rgb(15,70,140)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = 'rgb(18,82,166)';
                  }
                }}
                title={isDownloading ? 'Generating PDF...' : 'Download PDF Certificate'}
              >
                {isDownloading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-download mr-2"></i>
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Student Info Card */}
        <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100" style={{ fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            {[
              { icon: 'fa-user', label: 'Username', value: result.username },
              { icon: 'fa-id-card', label: 'Full Name', value: result.full_name || 'N/A' },
              { icon: 'fa-venus-mars', label: 'Gender', value: result.gender || 'N/A' },
              { icon: 'fa-building-columns', label: 'Institution', value: result.institution_name || 'N/A' },
              { icon: 'fa-book', label: 'Exam Topic', value: result.exam_topic_name || 'N/A' },
            ].map(({ icon, label, value }) => (
              <div key={label} className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="flex items-center mb-2">
                  <i className={`fa-solid ${icon} mr-2`} style={{ color: 'rgb(18,82,166)' }} />
                  <p className="text-sm text-gray-500">{label}</p>
                </div>
                <p className="font-normal text-gray-900 break-words">{value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Results Table */}
        <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
          {/* Show disqualified banner only for 2018 End Year */}
          {is2018EndYearDisqualified && (
            <div className="bg-red-50 border-b border-red-200 p-4">
              <div className="flex items-center justify-center">
                <i className="fa-solid fa-ban text-red-500 mr-3 text-xl"></i>
                <span className="text-red-700 font-bold text-lg">DISQUALIFIED</span>
              </div>
            </div>
          )}
          
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr style={{ backgroundColor: is2018EndYearDisqualified ? '#b91c1c' : 'rgb(18,82,166)' }}>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Examination Year</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Total Score</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">
                    {is2018EndYearDisqualified ? (
                      <span className="inline-flex items-center">
                        <i className="fa-solid fa-ban mr-2"></i>
                        Disqualified
                      </span>
                    ) : (
                      'Status'
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {resultsArray && resultsArray.length > 0 ? (
                  resultsArray.map((resultItem, index) => {
                    if (!resultItem) return null;
                    const scoreNum = getScoreNumber(resultItem.total_score);
                    return (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                          <div className="flex items-center">
                            <i className="fa-regular fa-calendar mr-3 text-lg" style={{ color: 'rgb(18,82,166)' }} />
                            <span className="text-sm font-medium text-gray-900">{resultItem.year || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">
                              {formatTotalScore(resultItem)}
                            </div>
                            {!isNgResult(resultItem) && !isPendingResult(resultItem) && (
                              <div className="ml-3 w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    scoreNum >= 50 ? 'bg-green-600' : 'bg-red-600'
                                  }`}
                                  style={{ width: `${Math.min(scoreNum || 0, 100)}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 sm:px-8 py-4 sm:py-6">
                          <div className="flex flex-col items-start">
                            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                              resultItem.status === 'Pass' 
                                ? 'bg-green-50 text-green-700 border border-green-100' 
                                : 'bg-red-50 text-red-700 border border-red-100'
                            }`}>
                              <i className={`fa-solid ${resultItem.status === 'Pass' ? 'fa-check-circle' : 'fa-times-circle'} mr-2`} />
                              {resultItem.status || 'Not Available'}
                              {is2018EndYearResult(resultItem) && resultItem.is_disqualified === 1 && (
                                <span className="ml-1 font-bold">(Disqualified)</span>
                              )}
                            </span>
                            {render2018EndYearTags(resultItem)}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="px-6 sm:px-8 py-8 text-center text-gray-500">
                      No results found. {result && result.results ? `Results data: ${JSON.stringify(result.results)}` : 'No results data available.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden">
            <div className="p-4">
              {resultsArray && resultsArray.length > 0 ? (
                resultsArray.map((resultItem, index) => {
                  if (!resultItem) return null;
                  const scoreNum = getScoreNumber(resultItem.total_score);
                  return (
                    <div key={index} className="mb-4 last:mb-0">
                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <i className="fa-regular fa-calendar mr-2 text-lg" style={{ color: 'rgb(18,82,166)' }} />
                            <span className="text-sm font-medium text-gray-700">Examination Year</span>
                          </div>
                        </div>
                        <p className="text-base font-semibold text-gray-900">{resultItem.year || 'N/A'}</p>
                      </div>

                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">Total Score</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-base font-semibold text-gray-900">
                            {formatTotalScore(resultItem)}
                          </p>
                        </div>
                        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                          {!isNgResult(resultItem) && !isPendingResult(resultItem) && (
                            <div 
                              className={`h-full rounded-full ${
                                scoreNum >= 50 ? 'bg-green-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${Math.min(scoreNum || 0, 100)}%` }}
                            />
                          )}
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">Status</span>
                        </div>
                        <div className="space-y-2">
                          <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
                            resultItem.status === 'Pass' 
                              ? 'bg-green-50 text-green-700 border border-green-100' 
                              : 'bg-red-50 text-red-700 border border-red-100'
                          }`}>
                            <i className={`fa-solid ${resultItem.status === 'Pass' ? 'fa-check-circle' : 'fa-times-circle'} mr-2`} />
                            {resultItem.status || 'Not Available'}
                            {is2018EndYearResult(resultItem) && resultItem.is_disqualified === 1 && (
                              <span className="ml-1 font-bold">(Disqualified)</span>
                            )}
                          </span>
                          {render2018EndYearTags(resultItem)}
                        </div>
                      </div>
                      
                      {index < resultsArray.length - 1 && (
                        <div className="border-t border-gray-200 my-4"></div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No results found. {result && result.results ? `Results data: ${JSON.stringify(result.results)}` : 'No results data available.'}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Note Section */}
        <section className="mt-12 p-6 sm:p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 shadow-md">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start">
              <i className="fa-solid fa-circle-info text-2xl flex-shrink-0 mt-1" style={{ color: 'rgb(18,82,166)' }} />
              <div className="ml-4">
                <h4 className="text-lg font-semibold mb-2" style={{ color: 'rgb(18,82,166)' }}>Important Note</h4>
                <p className="text-sm mb-2" style={{ color: 'rgb(18,82,166)' }}>
                  This is your official examination result. For any discrepancies or concerns, please submit a complaint.
                  {is2018EndYearPending ? ' Complaints are unavailable while your 2018 End Year result is on pending.' : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button 
                onClick={() => isComplaintAllowed && setShowComplaintModal(true)}
                className={`flex items-center justify-center px-6 py-3 rounded-lg shadow-md transition-colors ${
                  isComplaintAllowed ? 'text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                style={isComplaintAllowed ? { backgroundColor: 'rgb(18,82,166)' } : undefined}
                onMouseEnter={(e) => {
                  if (isComplaintAllowed) e.target.style.backgroundColor = 'rgb(15,70,140)';
                }}
                onMouseLeave={(e) => {
                  if (isComplaintAllowed) e.target.style.backgroundColor = 'rgb(18,82,166)';
                }}
                disabled={!isComplaintAllowed}
              >
                <i className="fa-solid fa-flag mr-2" />
                {isComplaintAllowed ? 'Submit Complaint' : 'Complaint unavailable'}
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Complaint Modal */}
      {showComplaintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-flag mr-3" style={{ color: 'rgb(18,82,166)' }}></i>
                  Submit Complaint
                </h2>
                <button
                  onClick={handleCancelComplaint}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleComplaintSubmit(); }}>
                {/* Category Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complaint Category *
                  </label>
                  <select
                    value={complaintData.category}
                    onChange={(e) => setComplaintData({ ...complaintData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select a category</option>
                    {complaintCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description * ({complaintData.description.length}/255)
                  </label>
                  <textarea
                    value={complaintData.description}
                    onChange={(e) => setComplaintData({ ...complaintData, description: e.target.value })}
                    rows={4}
                    maxLength={255}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      complaintData.description.length > 250 ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Please describe your complaint in detail..."
                    required
                  />
                  {complaintData.description.length > 250 && (
                    <p className="text-sm text-red-600 mt-1">
                      Warning: You're approaching the character limit ({complaintData.description.length}/255)
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number * (Format: 09*********)
                  </label>
                  <input
                    type="tel"
                    value={complaintData.phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 10) {
                        setComplaintData({ ...complaintData, phoneNumber: value });
                      }
                    }}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="09*********"
                    required
                  />
                  {complaintData.phoneNumber.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {complaintData.phoneNumber.length}/10 characters
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {validationError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{validationError}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelComplaint}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingComplaint || !isComplaintAllowed}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingComplaint ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Submitting...
                      </>
                    ) : (
                      'Submit Complaint'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            {/* Success Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-check-circle mr-3 text-green-500"></i>
                  Success
                </h2>
                <button
                  onClick={handleCloseSuccessModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            {/* Success Modal Body */}
            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-check text-2xl text-green-500"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Complaint Submitted
                </h3>
                <p className="text-gray-600 mb-6">
                  Your complaint has been submitted successfully. We have received your request and will review it shortly.
                </p>
                <div className="p-4 mb-4 rounded-lg border text-center" style={{ 
                  backgroundColor: 'rgba(18, 82, 166, 0.05)', 
                  borderColor: 'rgba(18, 82, 166, 0.2)'
                }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: 'rgb(18,82,166)' }}>
                    ⚠️ Save This Reference Number:
                  </p>
                  <div className="flex items-center justify-center mb-3">
                    <span className="font-mono font-bold text-base mr-3" style={{ color: 'rgb(18,82,166)' }}>{complaintReference}</span>
                    <button
                      onClick={copyReferenceNumber}
                      className="px-3 py-1 rounded text-sm font-medium hover:bg-blue-100 transition-colors"
                      style={{ color: 'rgb(18,82,166)' }}
                      title="Copy reference number"
                    >
                      <i className="fa-solid fa-copy mr-1"></i>
                      Copy
                    </button>
                    {copyConfirmed && (
                      <span className="ml-2 text-sm font-medium text-green-600 flex items-center">
                        <i className="fa-solid fa-check mr-1"></i>
                        Copied!
                      </span>
                    )}
                  </div>
                  <p className="text-xs opacity-80" style={{ color: 'rgb(18,82,166)' }}>
                    You can check the complaint result when announced using this reference number.
                  </p>
                </div>
              </div>
            </div>

            {/* Success Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <div className="flex justify-center">
                <button 
                  onClick={handleCloseSuccessModal}
                  className="px-8 py-3 text-white rounded-lg transition-colors duration-200 font-medium"
                  style={{ backgroundColor: 'rgb(18,82,166)' }}
                >
                  <i className="fa-solid fa-check mr-2"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Download Popup removed */}
    </div>
  );
}


